import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { StatusCode } from "api/protocols";
import { DeleteFacilityFavourite, PostFacilityFavourite } from "api/rpc/2024-04/customer/facility";
import classNames from "classnames";
import Input from "components/form/input";
import Spin from "components/spin/spin";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";
import { useAppDispatch } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import React, { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { showError, showSuccess } from "redux/actions/ui";
import { sortFacilities } from "redux/reducers/clientPortal";
import { IFacility } from "redux/reducers/models/facility";
import { IFacilityPortalExtended, useTeeOnPortalContext } from "../../TeeOnPortalContext";
import "./teeOnPortalFacilities.scss";

interface IProps {
  /**Dropdown visibility */
  open: boolean;
  /**The current facility this dropdown is displayed under */
  currentFacility?: IFacility;
  /**Array of facilities to display */
  facilities: Array<IFacility>;
  /**Facilities that are currently selected */
  selectedFacilities: Array<IFacility>;
  /**Swap selected facility with new facility */
  onFacilitySwap?: (facilities: Array<IFacility>) => void;
  /**Add a new selected facility */
  onFacilityAdd?: (facilities: Array<IFacility>) => void;
  /**Handle close dropdown */
  onClose: () => void;
  /*Position of the dropdown relative to the parent container */
  position?: "left" | "center" | "right";
}

interface IState {
  search: string;
}

/**Set as child component of relative positioned parent component */
export default function FacilitiesDropdown(props: IProps) {
  const {
    open,
    currentFacility,
    facilities,
    selectedFacilities,
    onFacilityAdd,
    onFacilitySwap,
    onClose,
    position = "center",
  } = props;
  const [state, setState] = useState<IState>({
    search: "",
  });
  const windowSize = useWindowSize();
  const dispatch = useAppDispatch();
  const { teeOnPortalState, updateTeeOnPortal } = useTeeOnPortalContext();

  //Display spinner next to facility when adding or removing from favourites
  const [loadingFacilityId, setLoadingFacilityId] = useState<number>(null);

  useEffect(() => {
    if (!open) {
      setState(prevState => ({ ...prevState, search: "" }));
    }
  }, [open]);

  const filteredFacilities = useMemo(() => {
    if (state.search) {
      return facilities?.filter(
        facility =>
          facility?.long_name?.toLowerCase().includes(state?.search?.toLowerCase()) ||
          facility?.city?.toLowerCase()?.includes(state?.search?.toLowerCase()) ||
          facility?.postal?.toLocaleLowerCase()?.includes(state?.search?.toLowerCase()),
      );
    } else if (facilities) {
      return [...facilities];
    } else {
      return [];
    }
  }, [state.search, facilities]);

  function handleAddFacility(facility: IFacility, e: React.MouseEvent<HTMLLIElement, MouseEvent>) {
    e.stopPropagation();
    let updatedSelectedFacilities = [...selectedFacilities];
    if (onFacilitySwap) {
      updatedSelectedFacilities = updatedSelectedFacilities?.map(selectedFacility =>
        selectedFacility?.id === currentFacility?.id ? { ...facility } : selectedFacility,
      );
      void onFacilitySwap(updatedSelectedFacilities);
    } else if (onFacilityAdd) {
      updatedSelectedFacilities = [...selectedFacilities, facility];
      void onFacilityAdd(updatedSelectedFacilities);
    }
    void onClose();
  }

  async function handleAddToFavourites(e: React.MouseEvent, facility: IFacilityPortalExtended) {
    e.stopPropagation();
    setLoadingFacilityId(facility?.id);
    const favRes = await PostFacilityFavourite({ facility_id: facility?.id }, false);
    setLoadingFacilityId(null);
    if (favRes?.status !== StatusCode.OK) {
      dispatch(showError("Error adding course to favourites"));
      return;
    }
    void updateFavouriteStatus(facility);
    dispatch(showSuccess("Successfully added course to favourites"));
  }

  async function handleRemoveFromFavourites(e: React.MouseEvent, facility: IFacilityPortalExtended) {
    e.stopPropagation();
    setLoadingFacilityId(facility?.id);
    const favRes = await DeleteFacilityFavourite({ facility_id: facility?.id }, false);
    setLoadingFacilityId(null);
    if (favRes?.status !== StatusCode.OK) {
      dispatch(showError("Error removing course from favourites"));
      return;
    }
    void updateFavouriteStatus(facility);
    dispatch(showSuccess("Successfully removed course from favourites"));
  }

  function updateFavouriteStatus(selectedFacility: IFacilityPortalExtended) {
    const updatedFacilities = teeOnPortalState?.facilities?.map(facility =>
      facility?.id === selectedFacility?.id ? { ...facility, favourite: !facility?.favourite } : facility,
    );
    const updatedSelectedFacilities = teeOnPortalState?.selectedFacilities?.map(facility =>
      facility?.id === selectedFacility?.id ? { ...facility, favourite: !facility?.favourite } : facility,
    );
    const sortedFacilities = sortFacilities([...updatedFacilities]);
    updateTeeOnPortal({ facilities: sortedFacilities, selectedFacilities: updatedSelectedFacilities });
  }

  return (
    <>
      {windowSize.width <= MOBILE_WIDTH && open && (
        <div
          className="facilities-dropdown-backdrop "
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
        ></div>
      )}
      <div
        style={{
          display: open ? "block" : undefined,
          transform: open && windowSize.width <= MOBILE_WIDTH ? "translate(0, -400px)" : undefined,
        }}
        className={classNames("facilities-dropdown-container", {
          [`facilities-dropdown-container-${windowSize.width <= MOBILE_WIDTH ? "left" : position}`]:
            windowSize.width <= MOBILE_WIDTH ? "left" : position,
        })}
      >
        <div className="facilities-dropdown-input">
          <Input
            labelHidden
            placeholder="Name, City or Postal Code"
            onSearchClear={() => setState(prevState => ({ ...prevState, search: "" }))}
            onChange={e => setState(prevState => ({ ...prevState, search: e.target.value }))}
            value={state.search}
          />
        </div>

        <ul>
          {filteredFacilities?.map((facility, index) => {
            const selectedFacility = selectedFacilities?.some(
              selectedFacility => selectedFacility?.id === facility?.id,
            );
            return (
              <li
                key={index}
                className="facilities-dropdown-option"
                onClick={e => (!selectedFacility ? handleAddFacility(facility, e) : undefined)}
              >
                <div className="facilities-dropdown-option-container">
                  {facility?.logo_source ? (
                    <img src={facility?.logo_source} alt="Facility Logo" className="facilities-dropdown-logo" />
                  ) : (
                    <img
                      src="/public/images/default_profile_image.svg"
                      alt={"Facility Logo"}
                      className="facilities-dropdown-logo"
                    />
                  )}
                  <p className="facilities-dropdown-primary">{facility?.long_name}</p>
                  <p className="facilties-dropdown-secondary">{facility?.city},</p>
                  <p className="facilties-dropdown-secondary">{facility?.province_code}</p>
                  {windowSize.width > TABLET_WIDTH && !!teeOnPortalState?.user ? (
                    loadingFacilityId === facility?.id ? (
                      <div style={{ height: "1rem" }}>
                        <Spin />
                      </div>
                    ) : (
                      <p>
                        <OverlayTrigger
                          placement="auto"
                          overlay={
                            <Tooltip id="facilityFavouritesToolTip">
                              {facility?.favourite ? "Remove from favourites" : "Add to favourites"}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon
                            onClick={
                              loadingFacilityId
                                ? e => e.stopPropagation()
                                : facility?.favourite
                                ? e => handleRemoveFromFavourites(e, facility)
                                : e => handleAddToFavourites(e, facility)
                            }
                            size="1x"
                            fixedWidth
                            className={classNames("facilities-dropdown-unfavourite", {
                              "facilities-dropdown-favourite": facility?.favourite,
                            })}
                            icon={faStar}
                          />
                        </OverlayTrigger>
                      </p>
                    )
                  ) : !!teeOnPortalState?.user && facility?.favourite ? (
                    <p>
                      <FontAwesomeIcon
                        size="1x"
                        fixedWidth
                        className={classNames("facilities-dropdown-unfavourite", {
                          "facilities-dropdown-favourite": facility?.favourite,
                        })}
                        icon={faStar}
                      />
                    </p>
                  ) : null}
                </div>
                {selectedFacility && <FontAwesomeIcon icon={["far", "check"]} className="facilities-dropdown-check" />}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}
